import {
  Order,
  CustomerProductDetailsDto,
  CustomerDeliveryStatus,
  CustomerSellerDetailsDto,
} from "../../../Redux/custmoer_orders/types";
import {
  SalarOrder,
  OrderItem,
  OrderStatus,
} from "./OrdersTypes";

/**
 * Maps API delivery status to UI order status
 */
export const mapDeliveryStatusToOrderStatus = (
  status: CustomerDeliveryStatus
): OrderStatus => {
  const statusMap: Record<CustomerDeliveryStatus, OrderStatus> = {
    PENDING: "processing",
    IN_TRANSIT: "shipped",
    OUT_FOR_DELIVERY: "out-for-delivery",
    DELIVERED: "delivered",
    NOT_DELIVERED: "cancelled",
    ORDER_ACCEPTED: "processing",
    PREPARING: "processing",
    ORDER_READY: "processing",
    RIDER_ASSIGNED: "shipped",
    RIDER_ARRIVED_AT_RESTAURANT: "shipped",
    ORDER_PICKED_UP: "shipped",
    RIDER_ARRIVED_AT_LOCATION: "out-for-delivery",
    CANCELLED: "cancelled",
  };

  return statusMap[status] || "processing";
};

/**
 * Maps API delivery status to UI status title
 */
export const mapDeliveryStatusToTitle = (
  status: CustomerDeliveryStatus
): string => {
  const titleMap: Record<CustomerDeliveryStatus, string> = {
    PENDING: "Processing",
    IN_TRANSIT: "On the way",
    OUT_FOR_DELIVERY: "Arriving today",
    DELIVERED: "Delivered",
    NOT_DELIVERED: "Not delivered",
    ORDER_ACCEPTED: "Order accepted",
    PREPARING: "Preparing",
    ORDER_READY: "Ready for pickup",
    RIDER_ASSIGNED: "Rider assigned",
    RIDER_ARRIVED_AT_RESTAURANT: "Rider at restaurant",
    ORDER_PICKED_UP: "Order picked up",
    RIDER_ARRIVED_AT_LOCATION: "Rider at location",
    CANCELLED: "Cancelled",
  };

  return titleMap[status] || "Processing";
};

/**
 * Maps API delivery status to UI status message
 */
export const mapDeliveryStatusToMessage = (
  status: CustomerDeliveryStatus
): string => {
  const messageMap: Record<CustomerDeliveryStatus, string> = {
    PENDING: "Your order is being processed and will be shipped soon.",
    IN_TRANSIT: "Your shipment has left the fulfilment centre and is moving through the delivery network toward your city.",
    OUT_FOR_DELIVERY: "Your package is out for delivery. Keep your phone available in case the delivery partner needs help locating the address.",
    DELIVERED: "Package was delivered successfully and handed over at the selected delivery address.",
    NOT_DELIVERED: "Delivery attempt failed. Please contact support for assistance.",
    ORDER_ACCEPTED: "Your order has been accepted and is being prepared.",
    PREPARING: "Your order is being prepared with care.",
    ORDER_READY: "Your order is ready for pickup.",
    RIDER_ASSIGNED: "A rider has been assigned to deliver your order.",
    RIDER_ARRIVED_AT_RESTAURANT: "The rider has arrived at the restaurant to pick up your order.",
    ORDER_PICKED_UP: "The rider has picked up your order and is on the way.",
    RIDER_ARRIVED_AT_LOCATION: "The rider has arrived at your location.",
    CANCELLED: "This order has been cancelled.",
  };

  return messageMap[status] || "Your order is being processed.";
};

/**
 * Formats date to match UI expected format
 */
export const formatDateToUI = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch (error) {
    return dateString;
  }
};

const PLACEHOLDER_IMAGE = "/assets/images/logos/salarlogo.png";

/**
 * Transforms API product details to UI order item.
 * productId is a MongoDB ObjectId string and is preserved as-is (no parseInt).
 */
export const transformOrderItemToOrderItem = (
  apiItem: CustomerProductDetailsDto,
  sellerDetails?: CustomerSellerDetailsDto,
  index: number = 0
): OrderItem => {
  const sellerName =
    apiItem.sellerName || sellerDetails?.sellerName || "Salar Retail";
  return {
    id: apiItem.productId || `item-${index}`,
    productId: apiItem.productId || "",
    sourceId: apiItem.productId || "",
    sourceType: (apiItem as any).sourceType,
    title: apiItem.productName || "Product",
    // productType is a category tag ("Product"/"ECOMMERCE"), not a description
    description: "",
    image: apiItem.productImage || PLACEHOLDER_IMAGE,
    variant: apiItem.validity ? `Valid till ${apiItem.validTo}` : undefined,
    quantity: apiItem.quantity || 1,
    price: apiItem.unitPrice || apiItem.totalPrice || 0,
    mrp: undefined,
    sellerName,
    sellerSlug: sellerName.toLowerCase().replace(/\s+/g, "-"),
  };
};

/**
 * Transforms API order to UI order
 */
export const transformOrderToSalarOrder = (apiOrder: Order): SalarOrder => {
  const deliveryStatus = apiOrder.deliveryStatus;
  const orderStatus = mapDeliveryStatusToOrderStatus(deliveryStatus);
  const statusTitle = mapDeliveryStatusToTitle(deliveryStatus);
  const statusMessage = mapDeliveryStatusToMessage(deliveryStatus);

  // Calculate item prices from total (fallback when per-item price absent)
  const totalItems = apiOrder.productDetails?.length || 1;
  const finalPrice = apiOrder.finalAmount?.finalPrice || apiOrder.payableAmount || 0;
  const fallbackItemPrice = Math.round(finalPrice / totalItems);

  // Transform product details to order items
  const items = (apiOrder.productDetails || []).map((apiItem, index) => {
    const orderItem = transformOrderItemToOrderItem(apiItem, apiOrder.sellerDetails, index);
    return {
      ...orderItem,
      price: apiItem.unitPrice || apiItem.totalPrice || fallbackItemPrice,
    };
  });

  // Determine eligibility flags based on status
  const isDelivered = deliveryStatus === CustomerDeliveryStatus.DELIVERED;
  const isCancelled = deliveryStatus === CustomerDeliveryStatus.CANCELLED;
  const isReturned =
    apiOrder.refundDetails?.refundStatus === "COMPLETED" ||
    apiOrder.refundDetails?.refundStatus === "REFUNDED" ||
    apiOrder.refundStatus === "REFUNDED" ||
    apiOrder.paymentStatus === "REFUNDED" ||
    apiOrder.paymentStatus === "Refunded";

  // Prefer structured deliveryAddress; fall back to flat backward-compat fields
  const addr = apiOrder.deliveryAddress;

  return {
    id: apiOrder.orderId || apiOrder.id || "",
    orderNo: apiOrder.orderNo || apiOrder.orderNumber || "",
    placedDate: formatDateToUI(apiOrder.orderDate),
    total: finalPrice,
    payableAmount: apiOrder.payableAmount || 0,
    platformFee: apiOrder.finalAmount?.platformFee || 0,
    deliveryCharges: apiOrder.finalAmount?.deliveryCharges || 0,
    status: orderStatus,
    statusTitle,
    statusMessage,
    expectedDate: apiOrder.shipmentInfo?.deliveryEstimatedDays
      ? `Estimated ${apiOrder.shipmentInfo.deliveryEstimatedDays} day(s)`
      : undefined,
    paymentMethod: apiOrder.paymentDetails?.paymentInstrument || "Online",
    paymentStatus: apiOrder.paymentStatus || "",
    purchaseType: apiOrder.purchaseType || "",
    invoiceAvailable:
      apiOrder.paymentStatus === "COMPLETED" ||
      apiOrder.paymentStatus === "Success",
    returnEligible: isDelivered && !isReturned,
    sellerFeedbackEligible: isDelivered,
    deliveryFeedbackEligible: isDelivered,
    reviewEligible: isDelivered,
    shipTo: {
      name: addr?.fullName || apiOrder.deliveryFullName || "Self",
      city: addr?.city || apiOrder.deliveryCity || apiOrder.sellerDetails?.sellerLocation || "Unknown",
      state: addr?.state || apiOrder.deliveryState || "Unknown",
      pincode: addr?.pincode || apiOrder.deliveryPincode || "Unknown",
    },
    items,
    salarCoinsEarned: apiOrder.salarCoinsEarned || 0,
    trackingNumber: apiOrder.shipmentInfo?.trackingNumber || apiOrder.trackingNumber,
    trackingUrl: apiOrder.shipmentInfo?.trackingUrl || apiOrder.trackingUrl,
    gatewayTransactionId: apiOrder.paymentDetails?.gatewayTransactionId,
    offersApplied: {
      totalDiscount: apiOrder.offersApplied?.totalDiscount || 0,
      salarCodeDiscount: apiOrder.offersApplied?.salarCodeDiscount || 0,
      salarShoppingVoucher: apiOrder.offersApplied?.salarShoppingVoucher || 0,
      salarRuby: apiOrder.offersApplied?.salarRuby || 0,
    },
    couponDetails: apiOrder.couponDetails,
    shipmentInfo: apiOrder.shipmentInfo,
    deliveryAddress: apiOrder.deliveryAddress,
    refundDetails: {
      refundAmount: apiOrder.refundDetails?.refundAmount || 0,
      refundStatus:
        apiOrder.refundDetails?.refundStatus || apiOrder.refundStatus || "NOT_APPLICABLE",
    },
  };
};

/**
 * Transforms multiple API orders to UI orders
 */
export const transformOrdersToSalarOrders = (apiOrders: Order[]): SalarOrder[] => {
  return apiOrders.map(transformOrderToSalarOrder);
};
